import {
	Card,
	CardBody,
	CardHeader,
	CardHeaderActions,
	CardHeading,
	ConfirmationDialog,
	DeleteModelWithUsagesDialog,
	DetailsPageInfoField,
	DetailsPageInfoFields,
	FeedbackDetailsResource,
	FeedbackService,
	FeedbackUpdateDrawer,
	IconAnchorButton,
	IconButton,
	SlateViewer,
	Trigger,
	isSuccess,
	useFeedbackCrud,
	useModelUsage,
	useOpen,
	useToasts,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import styled from "styled-components";

const StyledCard = styled(Card)`
	height: max-content;
`;

interface Props {
	service: FeedbackService;
	feedback: FeedbackDetailsResource;
	canUpdate: boolean;
	canDelete: boolean;
	previewUrl: string;
}

export const FeedbackSettingsCardWithPreview: FC<Props> = ({ service, feedback, canUpdate, canDelete, previewUrl }) => {
	const { t } = useTranslation();
	const crudOperations = useFeedbackCrud(service);
	const resetProps = useOpen();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { showToast } = useToasts();
	const { handleUsage, ...usageModalProps } = useModelUsage(() =>
		service.usages(feedback.id).then((response) => response.data.data),
	);

	const handleDelete = useCallback(async () => {
		if (await crudOperations.deleteEntity(feedback.id)) {
			navigate("..", { replace: true });
			queryClient.removeQueries({ queryKey: service.showKey(feedback.id) });
			await queryClient.invalidateQueries({ queryKey: service.indexKey });
		}
	}, [crudOperations, feedback.id, navigate, queryClient, service]);

	const handleReset = useCallback(async () => {
		const res = await service.reset(feedback.id);
		if (isSuccess(res)) {
			showToast({ type: "success", title: t("Feedback"), body: t("Feedback has been reset.") });
			await queryClient.invalidateQueries({ queryKey: service.indexKey });
		}
		resetProps.handleClose();
	}, [feedback.id, queryClient, resetProps, service, showToast, t]);

	return (
		<>
			<DeleteModelWithUsagesDialog
				{...usageModalProps}
				modelName={t("Feedback")}
				confirmationBody={t("Are you sure you want to delete this feedback?")}
				deleteAction={handleDelete}
			/>
			<StyledCard>
				<CardHeader>
					<CardHeading>{t("Feedback")}</CardHeading>
					<CardHeaderActions>
						<IconAnchorButton
							href={previewUrl}
							target="_blank"
							rel="noreferrer"
							size="small"
							icon="arrow_outward"
							title={t("Show preview")}
						/>
						{canUpdate && !feedback.hasAnswers && (
							<Trigger
								render={(drawerProps) => (
									<FeedbackUpdateDrawer
										service={service}
										id={feedback.id}
										open={drawerProps.open}
										onClose={drawerProps.handleClose}
										onClosed={() => undefined}
										onSubmit={(entityFormState) => crudOperations.updateEntity(feedback.id, entityFormState)}
									/>
								)}
							>
								{(triggerProps) => (
									<IconButton {...triggerProps} title={t("Edit")} type="button" size="small" icon="edit" />
								)}
							</Trigger>
						)}
						{canDelete && !feedback.hasAnswers && (
							<IconButton
								title={t("Delete")}
								type="button"
								color="danger"
								size="small"
								onClick={handleUsage}
								icon="delete"
							/>
						)}
						{canUpdate && feedback.hasAnswers && (
							<IconButton
								title={t("Reset feedback")}
								type="button"
								size="small"
								onClick={resetProps.handleOpen}
								icon="refresh"
							/>
						)}
					</CardHeaderActions>
				</CardHeader>
				<CardBody>
					<DetailsPageInfoFields>
						<DetailsPageInfoField title={t("Name")} value={feedback.name} />
						<DetailsPageInfoField
							title={t("Description")}
							value={<SlateViewer content={feedback.description || []} />}
						/>
						<DetailsPageInfoField title={t("Number of questions")} value={feedback.questions.length} />
						{feedback.hasAnswers && (
							<DetailsPageInfoField title={t("Number of participants")} value={feedback.numberOfParticipants} />
						)}
					</DetailsPageInfoFields>
				</CardBody>
			</StyledCard>
			<ConfirmationDialog
				heading={t("Reset feedback")}
				labels={{ close: t("Close"), confirm: t("Confirm") }}
				open={resetProps.open}
				onClose={resetProps.handleClose}
				action={handleReset}
			>
				{t("Are you sure you want to reset this feedback?")}
			</ConfirmationDialog>
		</>
	);
};
